import { useCallback, useEffect, useRef } from 'react'

/**
 * Wraps `callback` so it runs only once `delayMs` has passed without another
 * invocation. Use to report user-typed input upward without firing per keystroke.
 *
 * Debouncing the call rather than the value keeps the trigger at the event that
 * caused it, so a caller reports the change from its handler instead of watching
 * a debounced copy of its own state. The returned function keeps a stable
 * identity, always invokes the latest `callback`, and drops a pending call when
 * the caller unmounts.
 */
export const useDebouncedCallback = <TArgs extends unknown[]>(
  callback: (...args: TArgs) => void,
  delayMs: number
): ((...args: TArgs) => void) => {
  const callbackRef = useRef(callback)
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  // Track the newest callback in a ref so a caller re-rendering with a fresh
  // closure doesn't change the debounced function's identity and restart the
  // timer mid-burst.
  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  useEffect(() => () => clearTimeout(timerRef.current), [])

  return useCallback(
    (...args: TArgs) => {
      clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => callbackRef.current(...args), delayMs)
    },
    [delayMs]
  )
}
